export type TimeOfDay = 'dawn' | 'day' | 'sunset' | 'night';
export type WeatherType = 'sunny' | 'rainy' | 'cloudy';

export interface GardenAtmosphere {
  timeOfDay: TimeOfDay;
  weather: WeatherType;
  autoRealTime: boolean; // Auto-sync with device real time
  isTimelapse: boolean;  // Accelerated day-night timelapse
  timelapseSpeed?: number; // Multiplier for timelapse simulation (e.g. 60x, 120x)
  autoWeather?: boolean; // Dynamic random weather transitions with realistic lifespans
  sunPosition: number;   // 0 (east/dawn) to 1 (west/sunset)
  minuteOfDay?: number;  // 0 - 1440 exact continuous minute of day
}

export type MediaType = 'photo' | 'video';

export type AuthorRole = 'novio' | 'novia' | 'ambos';

export interface Comment {
  id: string;
  author: string;
  authorRole?: AuthorRole;
  text: string;
  createdAt: string;
}

export type MemoryComment = Comment;

export interface MemoryItem {
  id: string;
  type: MediaType;
  mediaUrl: string; // Data URL, Blob URL, or external image (first/featured image)
  mediaUrls?: string[]; // Array of up to 100 photos/videos for manual swipe carousel
  place: string; // Lugar
  locationSource?: 'current_gps' | 'photo_exif' | 'none';
  latitude?: number;
  longitude?: number;
  locationName?: string;
  accuracyMeters?: number;
  locationCapturedAt?: string;
  locationPhotoIndex?: number;
  date: string; // Fecha (YYYY-MM-DD)
  message: string; // Mensaje debajo de la foto
  category: 'cita' | 'viaje' | 'aniversario' | 'cotidiano' | 'especial';
  likes?: number; // Campo heredado histórico (sistema de reacciones eliminado)
  isLiked?: boolean;
  reactedVisitorIds?: string[];
  reactedIps?: string[];
  comments?: Comment[];
  createdAt: string;
  authorRole?: AuthorRole;
  authorName?: string;
  authorAvatar?: string;
  ownerUid?: string;
  isPrivate?: boolean;
}

export interface VisitorInfo {
  ip: string;
  visitorId: string;
  docId: string;
  deviceType: 'Móvil' | 'Tablet' | 'Escritorio';

  // Geolocation Information (Country / City / Region)
  country?: string; // ej. "Colombia", "España", "México", "Estados Unidos"
  countryCode?: string; // ej. "CO", "ES", "MX", "US"
  city?: string; // ej. "Bogotá", "Madrid", "Ciudad de México", "Yopal"
  region?: string; // ej. "Cundinamarca", "Madrid", "Antioquia", "Casanare"
  flagEmoji?: string; // ej. "🇨🇴", "🇪🇸", "🇲🇽", "🇺🇸"

  // High Precision GPS Geolocation & Real Address
  latitude?: number; // Coordenada latitud exacta
  longitude?: number; // Coordenada longitud exacta
  accuracy?: number; // Precisión en metros (ej: 4.8 m)
  altitude?: number | null; // Altitud sobre el nivel del mar en metros
  altitudeAccuracy?: number | null;
  heading?: number | null;
  speed?: number | null;
  exactAddress?: string; // Dirección completa georreferenciada (calle, carrera, barrio, municipio)
  road?: string; // Calle, carrera, avenida o vía
  neighborhood?: string; // Barrio, sector o corregimiento
  postalCode?: string; // Código postal
  isp?: string; // Proveedor de internet u operador de red (Claro, Tigo, Movistar, etc.)
  mapsUrl?: string; // Enlace satelital de Google Maps con pin
  locationSource?: 'GPS_ALTA_PRECISION' | 'IP_APROXIMADA';
  locationPermissionStatus?: 'granted' | 'denied' | 'prompt' | 'unsupported';
  gpsTimestamp?: string;

  // Real Hardware Brand & Model
  brand?: string;
  model?: string;
  os: string;
  osVersion?: string;
  browser: string;
  browserVersion?: string;

  // Real Display & Screen
  screenResolution: string; // CSS viewport: ej. "408 × 907 px"
  physicalScreenResolution?: string; // Hardware real display: ej. "1080 × 2400 px (FHD+)"
  nativeScreenResolution?: string; // Resolución nativa de panel: ej. "1080 × 2400 px (FHD+)"
  devicePixelRatio?: number; // ej. 2.625
  colorDepth?: string; // ej. "24 bits"
  orientation?: string; // ej. "Vertical (Portrait)"

  // Hardware Fingerprint & Silicon
  gpuRenderer?: string; // ej. "Qualcomm Adreno (TM) 730"
  gpuVendor?: string;
  cpuCores?: number; // ej. 8 núcleos
  deviceMemory?: string; // ej. "8 GB RAM"
  maxTouchPoints?: number;
  connectionType?: string; // ej. "4G (Móvil)", "WiFi"
  batteryLevel?: string; // ej. "85% (Cargando)"
  macStatus?: string; // Descripción de MAC física y HW-UUID
  deviceFingerprint?: string; // Identificador seguro de hardware tipo HW-UUID
  hardwareSignature?: string;

  language: string;
  timezone: string;
  userAgent: string;
}

export interface VisitorLog extends VisitorInfo {
  id: string;
  firstVisitedAt: string;
  lastVisitedAt: string;
  lastActiveAt?: string; // Timestamp de actividad en vivo (heartbeat)
  isOnline?: boolean;
  visitCount: number;
  reactionsCount?: number;
  reactions?: Record<string, string>;
  ipHistory?: string[]; // Historial de todas las IPs públicas asociadas a este mismo dispositivo
}

export interface WebAnalyticsData {
  activeUsersCount: number;
  todayVisits: number;
  weekVisits: number;
  monthVisits: number;
  yearVisits: number;
  totalHistoricalVisits: number;
  mobileCount: number;
  desktopCount: number;
  tabletCount: number;
  mobilePercent: number;
  desktopPercent: number;
  tabletPercent: number;
  dailyBuckets: { dateStr: string; label: string; count: number }[];
  maxDayCount: number;
  recentLogs: VisitorLog[];
  status: 'loading' | 'available' | 'updating' | 'error';
  lastConfirmedAt?: string;
  errorMessage?: string;
}

export interface GardenFlower {
  id: string;
  flowerType?: 'golden-tulip' | 'pink-rose' | 'sunflower' | 'blue-hydrangea' | 'white-lily';
  growthStage?: 'sprout' | 'bud' | 'bloom';
}

export interface NavVisibilitySettings {
  garden: boolean; // Jardín Interactivo
  blog: boolean; // Álbum de Recuerdos
  letters: boolean; // Cartas Diarias
  animations: boolean; // Frases de Amor
  myWeb?: boolean; // Mi Web (Solicitud de web propia para parejas)
  myweb?: boolean; // Alias en minúsculas compatible con AppTab ('myweb')
}

export type WebRequestStatus =
  | 'enviado'
  | 'recibido'
  | 'en_revision'
  | 'cambios_solicitados'
  | 'aprobado'
  | 'publicado'
  | 'pending'
  | 'approved'
  | 'rejected'
  | 'rechazado';

export interface WebRequestPhaseInfo {
  phase: 1 | 2 | 3 | 4 | 5;
  key: 'enviado' | 'recibido' | 'en_revision' | 'cambios_solicitados' | 'aprobado' | 'publicado' | 'rechazado';
  title: string;
  subtitle: string;
  isApproved: boolean;
  isPublished: boolean;
}

export function getWebRequestPhaseInfo(status: WebRequestStatus): WebRequestPhaseInfo {
  const norm = (status || 'enviado').toLowerCase();

  if (norm === 'publicado') {
    return {
      phase: 5,
      key: 'publicado',
      title: 'Publicado',
      subtitle: 'Sitio web publicado y accesible para compartir',
      isApproved: true,
      isPublished: true,
    };
  }
  if (norm === 'aprobado' || norm === 'approved') {
    return {
      phase: 4,
      key: 'aprobado',
      title: 'Aprobado',
      subtitle: 'Sitio aprobado. Listo para publicación y previsualización',
      isApproved: true,
      isPublished: false,
    };
  }
  if (norm === 'cambios_solicitados') {
    return {
      phase: 3,
      key: 'cambios_solicitados',
      title: 'Cambios solicitados',
      subtitle: 'El administrador solicita ajustes en la información',
      isApproved: false,
      isPublished: false,
    };
  }
  if (norm === 'en_revision') {
    return {
      phase: 3,
      key: 'en_revision',
      title: 'En revisión',
      subtitle: 'El administrador está revisando y preparando tu sitio',
      isApproved: false,
      isPublished: false,
    };
  }
  if (norm === 'recibido') {
    return {
      phase: 2,
      key: 'recibido',
      title: 'Recepción',
      subtitle: 'Solicitud recibida y en cola de atención',
      isApproved: false,
      isPublished: false,
    };
  }
  if (norm === 'rejected' || norm === 'rechazado') {
    return {
      phase: 1,
      key: 'rechazado',
      title: 'Rechazado',
      subtitle: 'Solicitud no procesada',
      isApproved: false,
      isPublished: false,
    };
  }
  // Default is 'enviado' (or 'pending')
  return {
    phase: 1,
    key: 'enviado',
    title: 'Solicitud enviada',
    subtitle: 'Solicitud enviada exitosamente',
    isApproved: false,
    isPublished: false,
  };
}

export interface MyWebRequest {
  id: string;
  orderCode: string; // Código amigable ej. "WEB-8241"
  applicantName: string; // Nombre de la persona solicitante
  partnerName: string; // Nombre de su pareja
  whatsappPhone: string; // Número de WhatsApp para coordinar
  email?: string; // Correo electrónico opcional / de acceso
  assignedPassword?: string; // Contraseña asignada para acceder al panel admin de su web personalizada
  anniversaryDate?: string; // Fecha de inicio de relación o aniversario
  specialSong?: string; // Canción o melodía especial
  dedication?: string; // Dedicatoria o frase para la portada
  desiredFeatures?: string[]; // Características deseadas
  notes?: string; // Observaciones adicionales
  userNotes?: string; // Respuesta o notas del usuario a cambios solicitados
  status: WebRequestStatus; // 'enviado' | 'recibido' | 'en_revision' | 'cambios_solicitados' | 'aprobado' | 'publicado' | 'rechazado'
  createdAt: string; // Fecha y hora de solicitud
  updatedAt?: string;
  submittedAt?: string;
  receivedAt?: string;
  inReviewAt?: string;
  changesRequestedAt?: string;
  changesSubmittedAt?: string;
  allowUserEditing?: boolean; // Habilitado cuando el administrador solicita cambios, bloqueado automáticamente al enviar
  changesLocked?: boolean; // Indica si la edición está bloqueada esperando revisión del administrador
  approvedAt?: string;
  publishedAt?: string;
  rejectedAt?: string;
  adminNotes?: string;
  adminUid?: string;
  adminDecision?: string;
  isPublished?: boolean;
  visitorId?: string; // UUID del visitante para recordarlo en su dispositivo
  ownerUid?: string; // UID de Firebase Auth del usuario que envió la solicitud
  ip?: string; // IP detectada del solicitante
  deviceModel?: string; // Dispositivo desde el que envió la solicitud

  // Datos para su web personalizada y panel de administración propio:
  partner1Email?: string; // Correo de acceso del novio en su web personalizada (Firebase Auth)
  partner2Email?: string; // Correo de acceso de la novia en su web personalizada (Firebase Auth)
  customMemories?: MemoryItem[]; // Recuerdos y fotos de esta pareja
  customLetters?: DailyLetter[]; // Cartas de amor de esta pareja
  customSettings?: Partial<CoupleSettings>; // Ajustes específicos (nombres, dedicatoria, tema, etc.)
  customWatering?: GardenWateringState; // Estado de riego del jardín
  customTulips?: TulipSettings; // Colores de los tulipanes
}

export interface MyWebConfig {
  autoApproval: boolean; // Si es true, las solicitudes se aprueban automáticamente de inmediato
  whatsappAdminNumber: string; // Número de WhatsApp del admin con código de país
  whatsappMessageTemplate?: string; // Mensaje predeterminado al hacer clic en WhatsApp
  enabled?: boolean; // Si el módulo está activo
}

export interface BlockedIpEntry {
  ip: string;
  blockedAt: string;
  reason?: string;
  deviceModel?: string;
  visitorId?: string;
  country?: string;
  city?: string;
  flagEmoji?: string;
  blockedBy?: 'novio' | 'novia';
}

export interface SongTrack {
  id: string;
  title: string;
  artist: string;
  url: string; // URL de audio (streaming, data URL o archivo mp3)
  duration?: string; // ej: "3:42"
  uploadedBy?: 'novio' | 'novia';
  addedAt: string;
  isDefault?: boolean;
  notes?: string; // Dedicatoria o mensaje
  storageType?: 'cloud_chunks' | 'url' | 'synth';
  chunkCount?: number;
  ownerUid?: string;
  isPublic?: boolean;
  isPrivate?: boolean;
}

export interface TulipSettings {
  mode: 'all' | 'individual'; // Modo: 'all' (unificar todas) o 'individual' (cada una por separado)
  allColor: string; // Preset id (ej: 'golden', 'ruby', 'pink') o color hexadecimal personalizado
  individualColors: string[]; // Colores individuales para cada tulipán [tulipan 1, tulipan 2, tulipan 3, tulipan 4]
}

export interface GardenWateringState {
  lastWateredAt: string; // Fecha ISO del último riego
  wateredCount: number; // Veces que se ha regado el jardín
  lastWateredBy?: string; // Quién regó la última vez (ej: "Visitante", "El Novio", "La Novia")
  wateringIntervalDays?: number; // Días para marchitamiento completo (3 días por defecto)
  canWaterLevel?: number; // Nivel de agua de la regadera (0 a 100%). Se reduce con cada riego y se reinicia cada 24h tras su uso
  lastWaterRefillDate?: string; // Fecha (YYYY-MM-DD) del último reinicio diario de la regadera a 100%
  lastCanUsedAt?: string; // Fecha ISO exacta de cuando se usó por última vez la regadera (inicia el contador de 24 horas para recarga)
  flowerWateredAt?: string[]; // Fechas individuales de riego para cada una de las flores individuales
  witheringEnabled?: boolean;
}

export type DownloadLinkDuration = '3_hours' | '1_day' | '1_week' | '1_month' | 'never';

export interface AppDownloadLink {
  id: string;
  token: string;
  label?: string;
  duration: DownloadLinkDuration;
  createdAt: string; // ISO string
  expiresAt: string | null; // ISO string or null for 'never'
  downloadsCount: number;
  createdBy?: 'novio' | 'novia';
  isActive: boolean;
}

export interface AppKillSwitchSettings {
  isEnabled: boolean; // Si es true, la app queda inhabilitada o exige actualizar
  mode?: 'force_update' | 'maintenance'; // 'force_update' (exige versión mínima) o 'maintenance' (bloqueo total para todos)
  targetScope?: 'app_only' | 'all'; // 'app_only' (por defecto: aplica EXCLUSIVAMENTE a la app instalada / PWA, nunca a la web) o 'all'
  title?: string; // Título mostrado en la pantalla de bloqueo (ej: "Actualización Obligatoria Requerida")
  message?: string; // Mensaje explicativo para los usuarios
  minVersion?: string; // Versión mínima exigida (ej: "2.0.0")
  currentVersion?: string; // Versión actual publicada (ej: "2.0.0")
  updateButtonText?: string; // Texto del botón de actualización (ej: "Actualizar App Ahora")
  updateActionType?: 'auto' | 'download_page' | 'custom_url'; // 'auto' (recarga / PWA refresh), 'download_page' (/descargar-app), 'custom_url'
  customUpdateUrl?: string; // URL externa personalizada si aplica
  allowAdminBypass?: boolean; // Permite a los administradores entrar con su PIN
  disabledAt?: string; // Fecha y hora ISO en que se inhabilitó
  disabledBy?: 'novio' | 'novia'; // Quién inhabilitó la app
  notes?: string; // Notas internas de control
}

export interface CoupleSettings {
  partner1Name: string; // Nombre de la pareja 1 (novio)
  partner2Name: string; // Nombre de la pareja 2 (novia)
  partner1BirthDate?: string; // Fecha de nacimiento de la pareja 1 (YYYY-MM-DD)
  partner2BirthDate?: string; // Fecha de nacimiento de la pareja 2 (YYYY-MM-DD)
  partner1BloodType?: string; // Tipo de sangre de la pareja 1 (ej: O+, A+, etc.)
  partner2BloodType?: string; // Tipo de sangre de la pareja 2 (ej: O+, A+, etc.)
  partner1Title?: string; // Título o apodo de la pareja 1
  partner2Title?: string; // Título o apodo de la pareja 2
  partner1Avatar?: string; // Avatar / foto de la pareja 1
  partner2Avatar?: string; // Avatar / foto de la pareja 2
  partner1Email?: string; // Correo de acceso oficial del novio (Firebase Auth)
  partner2Email?: string; // Correo de acceso oficial de la novia (Firebase Auth)
  partner1Uid?: string; // UID real de Firebase Auth del novio
  partner2Uid?: string; // UID real de Firebase Auth de la novia
  hasAdminConfigured?: boolean; // Indica si ya se ha configurado la primera cuenta de administrador principal
  startDate: string; // ISO date string de la fecha de inicio
  quote: string;
  location: string;
  theme: 'sky-garden' | 'sunset-blush' | 'starlight-night';
  musicEnabled: boolean;
  playlist?: SongTrack[]; // Variedad de canciones subidas o configuradas desde admin
  activeSongId?: string; // Canción actualmente seleccionada para sonar en la web
  musicVolume?: number; // Volumen por defecto (0 a 1)
  musicAutoplay?: boolean; // Reproducción automática tras primera interacción
  musicLoopMode?: 'all' | 'one' | 'none'; // Modo de bucle
  atmosphereMode?: 'auto' | 'manual' | 'timelapse'; // Modo automático por reloj o manual desde admin
  manualTimeOfDay?: TimeOfDay; // 'dawn' | 'day' | 'sunset' | 'night'
  manualWeather?: WeatherType; // 'sunny' | 'rainy' | 'cloudy'
  manualMinuteOfDay?: number; // 0 - 1440
  tulipSettings?: TulipSettings; // Configuración de colores de los tulipanes (todas o cada una)
  gardenWatering?: GardenWateringState; // Sistema de riego e hidratación de las flores (marchitamiento en 3 días)
  navVisibility?: NavVisibilitySettings; // Visibilidad de interfaces en la barra de navegación
  myWebConfig?: MyWebConfig; // Configuración de adquisición de web propia y modo de auto-aprobación
  appDownloadLinks?: AppDownloadLink[]; // Enlaces de descarga directa de la App con expiración
  totalAppDownloads?: number; // Contador total acumulado de descargas de la app
  blockedIps?: string[]; // Lista de direcciones IP bloqueadas por el administrador
  blockedVisitorIds?: string[]; // Lista de identificadores únicos de dispositivo bloqueados para cerrojo instantáneo
  blockedIpEntries?: Record<string, BlockedIpEntry>; // Registro de auditoría de IPs bloqueadas
  partner1Ips?: string[]; // Direcciones IP reconocidas de la pareja 1
  partner1Devices?: string[]; // Modelos o huellas de dispositivo de la pareja 1
  partner2Ips?: string[]; // Direcciones IP reconocidas de la pareja 2
  partner2Devices?: string[]; // Modelos o huellas de dispositivo de la pareja 2
  appKillSwitch?: AppKillSwitchSettings; // Inhabilitar app y forzar actualización remota
  webMaintenance?: WebMaintenanceSettings; // Modo de mantenimiento para la página web
  maintenanceEnabled?: boolean;
  maintenanceTitle?: string;
  maintenanceMessage?: string;
  maintenanceReturnTime?: string;
  maintenanceUpdatedAt?: any;
  maintenanceUpdatedBy?: string;
}

export interface WebMaintenanceSettings {
  isEnabled: boolean; // ¿Está la web actualmente en mantenimiento?
  maintenanceEnabled?: boolean; // Alias estándar requerido
  title?: string; // Título visible en la pantalla (ej. "Estamos preparando algo bonito")
  maintenanceTitle?: string; // Alias estándar
  message?: string; // Mensaje explicativo para los visitantes
  maintenanceMessage?: string; // Alias estándar
  estimatedReturn?: string; // Tiempo o fecha estimada de regreso (opcional)
  maintenanceReturnTime?: string; // Alias estándar
  allowAdminBypass?: boolean; // Permitir acceso inmediato a administradores
  enabledAt?: string; // Fecha ISO cuando se activó el mantenimiento
  updatedAt?: string;
  maintenanceUpdatedAt?: any; // serverTimestamp() de Firestore
  enabledBy?: string; // UID del administrador que activó
  maintenanceUpdatedBy?: string; // UID del administrador autenticado
}

export type LetterCategory =
  | 'daily'
  | 'morning'
  | 'night'
  | 'love'
  | 'gratitude'
  | 'special'
  | 'yellow-flowers'
  | 'anniversary'
  | 'birthday'
  | 'valentines'
  | 'custom';

export interface DailyLetterViewRecord {
  openedAt: string; // Fecha y hora ISO exacta de apertura
  viewerName: string; // Quién vio la carta (nombre de perfil o "Visitante")
  deviceModel?: string; // Modelo del dispositivo o navegador
  ip?: string;
  city?: string;
  country?: string;
  browser?: string;
  os?: string;
  isMatchedPartner?: boolean;
}

export interface DailyLetter {
  id: string;
  title: string;
  subtitle: string;
  category: LetterCategory;
  customCategoryName?: string;
  date?: string; // Fecha de la carta (YYYY-MM-DD o ISO)
  unlockDate?: string; // Fecha programada para apertura automática
  isUnlocked?: boolean; // Para cartas con fecha programada
  isPrivate?: boolean; // Si es true, es carta privada protegida por PIN de 4 dígitos
  ownerUid?: string;
  themeColor: 'amber' | 'yellow' | 'rose' | 'purple' | 'emerald' | 'blue' | 'indigo';
  iconName?: 'flower' | 'cake' | 'heart' | 'sparkles' | 'sun' | 'moon' | 'mail';
  coverImage?: string;
  envelopeStyle?: 'classic' | 'romantic' | 'vintage' | 'stars' | 'golden';
  envelopeDesign?: 'classic-gold' | 'rose-blush' | 'starlight' | 'vintage-kraft' | 'emerald-silk';
  waxSeal?: 'heart' | 'flower' | 'kiss' | 'star' | 'sun' | 'sparkles';
  waxSealIcon?: 'heart' | 'flower' | 'kiss' | 'star' | 'sun' | 'sparkles';
  isFavorite?: boolean;
  isRead?: boolean;
  firstOpenedAt?: string; // Fecha y hora de la primera apertura
  lastOpenedAt?: string; // Fecha y hora de la última apertura
  openedBy?: string; // Nombre del último o primer usuario que la abrió
  views?: DailyLetterViewRecord[]; // Historial de todas las veces que fue abierta y por quién
  letterContent: {
    recipientName?: string;
    senderName?: string;
    openingSalutation?: string;
    bodyParagraphs: string[];
    closingSalutation?: string;
    signature?: string;
    pSMessage?: string;
  };
  interactiveSurprise?: {
    type?: 'none' | 'bouquet-yellow' | 'birthday-cake' | 'fireworks' | 'letter-envelope' | 'hearts' | 'rose-rain';
    mediaUrls?: string[];
    customSongName?: string;
  };
  createdAt: string;
}

// Alias for backwards compatibility if needed
export type SpecialEvent = DailyLetter;

export interface TimeElapsed {
  years: number;
  months: number;
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
  totalDays: number;
}
